import { useState, useMemo } from "react";
import { toast } from "sonner";
import {
  isExternalOnlyCarrier,
  useGetCarriers,
  useUpdateCarrier,
  type Carrier,
} from "@/api/carriers";
import { AddCarrierDialog } from "./add-carrier-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Search,
  MoreHorizontal,
  Settings,
  Activity,
  RefreshCw,
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  TrendingUp,
  Zap,
  Package,
  Building2,
  Users,
  Cloud,
  AlertCircle,
  UserPlus,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { CarrierAliasDialog } from "./carrier-alias-dialog";
import { ResolutionRuleDialog } from "./resolution-rule-dialog";
import { EditCarrierDialog } from "./edit-carrier-dialog";
import { InviteCarrierPortalUserDialog } from "./invite-carrier-portal-user-dialog";
import { CarrierPortalTeamDialog } from "./carrier-portal-team-dialog";
import { cn } from "@/lib/utils";

// Remove this interface - we now use the API-provided health and credentials

// Health and credential data now comes from the API

function usesOnlyLocalRates(carrier: Carrier) {
  return (
    carrier.selfRated &&
    (carrier.channels?.length ?? 0) > 0 &&
    carrier.channels?.every((channel) => channel.apiProviderId === null)
  );
}

export function CarriersTable() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCarrier, setSelectedCarrier] = useState<Carrier | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [aliasDialogOpen, setAliasDialogOpen] = useState(false);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [invitePortalUserCarrier, setInvitePortalUserCarrier] =
    useState<Carrier | null>(null);
  const [portalTeamCarrier, setPortalTeamCarrier] = useState<Carrier | null>(
    null
  );
  const [expandedServices, setExpandedServices] = useState<Set<string>>(
    new Set()
  );

  const queryClient = useQueryClient();

  const { data: carriersResponse, isLoading, refetch } = useGetCarriers();
  const updateCarrierMutation = useUpdateCarrier({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["carriers"] });
      toast.success("Carrier Updated", {
        description: "Carrier settings have been updated successfully.",
      });
    },
    onError: () => {
      toast.error("Failed to update carrier settings.");
    },
  });

  const carriers = useMemo(() => {
    if (!carriersResponse?.data) return [];

    // Deliberately NOT filtered by the top-bar carrier scope: this screen is
    // where admins MANAGE carriers — including externally procured ones (FedEx,
    // ChitChats…). The scope picker only offers SELF-RATED carriers (it exists
    // for operational screens), so applying it here hid every external carrier
    // even though they're active in the DB. Search is the only filter.
    return carriersResponse.data.filter(
      (c) =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.code.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [carriersResponse, searchTerm]);

  const handleToggle = (carrier: Carrier) => {
    updateCarrierMutation.mutate({
      id: carrier.id,
      data: {
        active: !carrier.active,
      },
    });
  };

  /**
   * How EZX procures from this carrier.
   *
   * `selfRated` is the only fact the API gives us: priced from OUR rate tables
   * (true) vs bought through an integration (false). WHICH integration is not
   * a boolean — it comes from the carrier's supply channels, so the provider
   * badges below fall back to a generic label when we have no channel detail.
   */
  const getSupplyBadge = (carrier: Carrier) => {
    if (carrier.activeChannelCount === 0) {
      return (
        <div className="flex flex-col gap-1">
          <Badge variant="outline">No active supply</Badge>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto justify-start p-0"
            aria-label={`Set up supply for ${carrier.name}`}
            onClick={() => {
              setSelectedCarrier(carrier);
              setEditDialogOpen(true);
            }}
          >
            Set up supply
          </Button>
        </div>
      );
    }
    if (carrier.selfRated) {
      return (
        <div className="flex flex-col gap-1">
          <Badge className="bg-green-50 text-green-700 border-green-200">
            <Building2 className="h-3 w-3 mr-1" />
            Self-rated
          </Badge>
          <span className="text-xs text-muted-foreground">
            Priced from our rate tables
          </span>
        </div>
      );
    }

    const providers = carrier.providers ?? [];
    if (providers.length > 0) {
      return (
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap gap-1">
            {providers.map((cp) => (
              <Badge
                key={cp.id}
                variant="outline"
                className={getProviderBadgeStyle(cp.apiProvider.code)}
              >
                <Cloud className="h-3 w-3 mr-1" />
                {cp.apiProvider.name}
              </Badge>
            ))}
          </div>
          {providers.length > 1 && (
            <span className="text-xs text-green-600 font-medium">
              {providers.length} providers available
            </span>
          )}
          {carrier.autoDiscovered && (
            <span className="text-xs text-muted-foreground italic">
              Auto-discovered
            </span>
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-1">
        <Badge
          variant="outline"
          className="border-orange-200 bg-orange-50 text-orange-700"
        >
          <AlertCircle className="h-3 w-3 mr-1" />
          Externally procured
        </Badge>
        <span className="text-xs text-muted-foreground">
          Bought through an integration
        </span>
      </div>
    );
  };

  const getProviderBadgeStyle = (providerCode: string) => {
    switch (providerCode.toLowerCase()) {
      case "chitchats":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "shipstation":
        return "bg-indigo-50 text-indigo-700 border-indigo-200";
      case "easypost":
        return "bg-teal-50 text-teal-700 border-teal-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "healthy":
        return "text-green-600 bg-green-50";
      case "degraded":
        return "text-yellow-600 bg-yellow-50";
      case "offline":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  return (
    <div className="space-y-6">
      {/* Main Table Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Carrier Configuration</CardTitle>
              <CardDescription>
                Manage carrier settings, credentials, and supply connections
              </CardDescription>
              <p className="text-sm text-muted-foreground">
                Connection checks do not confirm that a carrier can quote orders
                or fulfill shipments. Services, rates, coverage and operational
                setup need separate verification.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRuleDialogOpen(true)}
              >
                <Settings className="h-4 w-4 mr-2" />
                Resolution Rules
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAliasDialogOpen(true)}
              >
                <Settings className="h-4 w-4 mr-2" />
                Carrier Aliases
              </Button>
              <AddCarrierDialog onSuccess={() => refetch()} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search carriers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-[300px]"
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() =>
                queryClient.invalidateQueries({ queryKey: ["carriers"] })
              }
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">Status</TableHead>
                <TableHead>Carrier</TableHead>
                <TableHead>Supply</TableHead>
                <TableHead>Services</TableHead>
                <TableHead>Credentials</TableHead>
                <TableHead>Connection check</TableHead>
                <TableHead>Connection metrics</TableHead>
                <TableHead className="text-center">Enabled</TableHead>
                <TableHead className="w-[240px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : carriers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="text-center py-8 text-gray-500"
                  >
                    No carriers found
                  </TableCell>
                </TableRow>
              ) : (
                carriers.map((carrier) => (
                  <TableRow key={carrier.id}>
                    <TableCell>
                      <div className="flex items-center justify-center">
                        {carrier.active ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        ) : (
                          <XCircle className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {carrier.logoUrl ? (
                          <img
                            src={carrier.logoUrl}
                            alt={`${carrier.name} logo`}
                            className="h-8 w-8 object-contain"
                          />
                        ) : (
                          <Package className="h-6 w-6 text-gray-400" />
                        )}
                        <div>
                          <p className="font-medium">{carrier.name}</p>
                          <p className="text-xs text-gray-500">
                            {carrier.code}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getSupplyBadge(carrier)}</TableCell>
                    <TableCell>
                      {(() => {
                        // Phase 15b: services sourced from CarrierService rows
                        // on the server. Empty arrays surface the setup action.
                        const services = carrier.services ?? [];
                        if (services.length > 0) {
                          const isExpanded = expandedServices.has(carrier.id);
                          const displayedServices = isExpanded
                            ? services
                            : services.slice(0, 3);
                          return (
                            <div className="space-y-1">
                              <div className="flex flex-wrap gap-1">
                                {displayedServices.map((service) => (
                                  <Badge
                                    key={service.code}
                                    variant="outline"
                                    className="text-xs"
                                    title={
                                      service.displayName ??
                                      service.name ??
                                      service.description
                                    }
                                  >
                                    {service.code ?? service.name ?? "Unknown"}
                                  </Badge>
                                ))}
                                {services.length > 3 && (
                                  <Badge
                                    variant="secondary"
                                    className="text-xs cursor-pointer hover:bg-secondary/80 transition-colors"
                                    onClick={() => {
                                      const newExpanded = new Set(
                                        expandedServices
                                      );
                                      if (isExpanded) {
                                        newExpanded.delete(carrier.id);
                                      } else {
                                        newExpanded.add(carrier.id);
                                      }
                                      setExpandedServices(newExpanded);
                                    }}
                                  >
                                    {isExpanded ? (
                                      <>Show less</>
                                    ) : (
                                      <>+{services.length - 3} more</>
                                    )}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {services.length} service
                                {services.length === 1 ? "" : "s"} configured
                              </p>
                            </div>
                          );
                        }

                        if (
                          carrier.services?.length === 0 ||
                          carrier.serviceCount === 0
                        ) {
                          return (
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">
                                No shipping services
                              </p>
                              <Button
                                type="button"
                                variant="link"
                                size="sm"
                                className="h-auto p-0"
                                aria-label={`Set up services for ${carrier.name}`}
                                onClick={() => {
                                  setSelectedCarrier(carrier);
                                  setEditDialogOpen(true);
                                }}
                              >
                                Set up services
                              </Button>
                            </div>
                          );
                        }

                        return (
                          <div className="text-xs text-muted-foreground">—</div>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {carrier.credentials?.configured ? (
                        <div className="space-y-1">
                          <Badge className="bg-green-50 text-green-700">
                            <Shield className="h-3 w-3 mr-1" />
                            Configured
                          </Badge>
                          {carrier.credentials.provider && (
                            <p className="text-xs text-muted-foreground">
                              {carrier.credentials.provider}
                            </p>
                          )}
                        </div>
                      ) : carrier.credentials?.required === false ? (
                        <Badge variant="secondary">Not Required</Badge>
                      ) : (
                        <div className="space-y-1">
                          <Badge
                            variant="outline"
                            className="border-yellow-200 bg-yellow-50 text-yellow-700"
                          >
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Setup Required
                          </Badge>
                          {carrier.credentials?.fields && (
                            <div className="text-xs text-muted-foreground">
                              {carrier.credentials.fields
                                .filter((f) => !f.configured)
                                .map((f) => f.name)
                                .join(", ")}
                            </div>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {usesOnlyLocalRates(carrier) ? (
                          <>
                            <Badge variant="secondary">
                              No external connection
                            </Badge>
                            <p className="text-xs text-muted-foreground">
                              Local rates; setup not verified
                            </p>
                          </>
                        ) : (
                          <>
                            <Badge
                              className={cn(
                                getStatusColor(
                                  carrier.health?.status || "offline"
                                )
                              )}
                            >
                              {carrier.health?.status || "Unknown"}
                            </Badge>
                            {carrier.health?.message && (
                              <p
                                className="text-xs text-muted-foreground max-w-[150px] truncate"
                                title={carrier.health.message}
                              >
                                {carrier.health.message}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-xs">
                          <Clock className="h-3 w-3 text-gray-400" />
                          <span>
                            {(usesOnlyLocalRates(carrier)
                              ? undefined
                              : carrier.health?.responseTime) ?? "—"}{" "}
                            ms
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-xs">
                          <TrendingUp className="h-3 w-3 text-gray-400" />
                          <span>
                            {(usesOnlyLocalRates(carrier)
                              ? undefined
                              : carrier.health?.successRate) ?? "—"}
                            %
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-center">
                        <Switch
                          checked={carrier.active}
                          onCheckedChange={() => handleToggle(carrier)}
                          disabled={updateCarrierMutation.isPending}
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {!isExternalOnlyCarrier(carrier) && (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setInvitePortalUserCarrier(carrier)
                              }
                            >
                              <UserPlus className="h-4 w-4" />
                              Invite
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setPortalTeamCarrier(carrier)}
                            >
                              <Users className="h-4 w-4" />
                              Users
                            </Button>
                          </>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`More actions for ${carrier.name}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedCarrier(carrier);
                                setEditDialogOpen(true);
                              }}
                            >
                              <Settings className="h-4 w-4 mr-2" />
                              Edit Configuration
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Zap className="h-4 w-4 mr-2" />
                              Test Connection
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Activity className="h-4 w-4 mr-2" />
                              View Activity Log
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => handleToggle(carrier)}
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              {carrier.active ? "Disable" : "Enable"} Carrier
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialogs */}
      {selectedCarrier && (
        <EditCarrierDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedCarrier(null);
          }}
          carrier={selectedCarrier}
          channels={
            carriersResponse?.data.find(
              (carrier) => carrier.id === selectedCarrier.id
            )?.channels
          }
        />
      )}

      <CarrierAliasDialog
        open={aliasDialogOpen}
        onClose={() => setAliasDialogOpen(false)}
      />

      <ResolutionRuleDialog
        open={ruleDialogOpen}
        onClose={() => setRuleDialogOpen(false)}
      />

      {invitePortalUserCarrier && (
        <InviteCarrierPortalUserDialog
          open
          onClose={() => setInvitePortalUserCarrier(null)}
          carrierId={invitePortalUserCarrier.id}
          carrierName={invitePortalUserCarrier.name}
        />
      )}

      {portalTeamCarrier && (
        <CarrierPortalTeamDialog
          open
          onClose={() => setPortalTeamCarrier(null)}
          onInvite={() => {
            setInvitePortalUserCarrier(portalTeamCarrier);
            setPortalTeamCarrier(null);
          }}
          carrierId={portalTeamCarrier.id}
          carrierName={portalTeamCarrier.name}
        />
      )}
    </div>
  );
}
